import Phaser from "phaser";

/**
 * Sound Manager for handling all game audio
 * Manages sound effects, background music, and audio settings
 */
export class SoundManager {
  private scene: Phaser.Scene;
  private sounds: Map<string, Phaser.Sound.BaseSound> = new Map();
  private backgroundMusic: Phaser.Sound.BaseSound | null = null;
  private isMuted: boolean = false;
  private masterVolume: number = 0.7;
  private sfxVolume: number = 0.8;
  private musicVolume: number = 0.6;
  private queuedPlays: Array<{
    key: string;
    options?: { volume?: number; loop?: boolean };
  }> = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    // Sounds are loaded in LoadingScene, not here
  }

  /**
   * Initialize sounds after loading
   */
  public initializeSounds(): void {
    // Create sound instances
    this.sounds.set(
      "start-music",
      this.scene.sound.add("start-music", {
        loop: false,
        volume: this.sfxVolume,
      })
    );
    this.sounds.set(
      "coffee-break",
      this.scene.sound.add("coffee-break", {
        loop: true,
        volume: this.musicVolume,
      })
    );
    this.sounds.set(
      "eating-ghost",
      this.scene.sound.add("eating-ghost", { volume: this.sfxVolume })
    );
    this.sounds.set(
      "killed-by-ghost",
      this.scene.sound.add("killed-by-ghost", { volume: this.sfxVolume })
    );
    this.sounds.set(
      "ghost-normal-move",
      this.scene.sound.add("ghost-normal-move", {
        loop: true,
        volume: this.sfxVolume * 0.5,
      })
    );
    this.sounds.set(
      "ghost-scared-mode",
      this.scene.sound.add("ghost-scared-mode", {
        loop: true,
        volume: this.sfxVolume * 0.5,
      })
    );
    this.sounds.set(
      "ghost-return-home",
      this.scene.sound.add("ghost-return-home", { volume: this.sfxVolume })
    );
    this.sounds.set(
      "new-life",
      this.scene.sound.add("new-life", { volume: this.sfxVolume })
    );
    this.sounds.set(
      "credit-sound",
      this.scene.sound.add("credit-sound", { volume: this.sfxVolume })
    );
  }

  /**
   * Play background music
   */
  public playBackgroundMusic(musicKey: string): void {
    if (this.isMuted) return;

    // Stop current background music
    this.stopBackgroundMusic();

    // If audio is locked (no user interaction yet), queue the request
    if (this.scene.sound.locked) {
      this.scene.sound.once(Phaser.Sound.Events.UNLOCKED, () => {
        this.playBackgroundMusic(musicKey);
      });
      return;
    }

    const music = this.sounds.get(musicKey);
    if (music) {
      this.backgroundMusic = music as Phaser.Sound.BaseSound;
      // Type assertion for Phaser sound manager internal properties
      const soundManager = this.scene.sound as Phaser.Sound.BaseSoundManager & {
        usingWebAudio?: boolean;
      };
      const bg = this.backgroundMusic as Phaser.Sound.BaseSound & {
        isDecoded?: boolean;
        play: () => void;
      };
      if (soundManager.usingWebAudio && !bg.isDecoded) {
        this.scene.sound.once(Phaser.Sound.Events.DECODED, (key: string) => {
          if (key === musicKey) {
            bg.play();
          }
        });
        return;
      }
      bg.play();
    }
  }

  /**
   * Stop background music
   */
  public stopBackgroundMusic(): void {
    if (this.backgroundMusic) {
      this.backgroundMusic.stop();
      this.backgroundMusic = null;
    }
  }

  /**
   * Play sound effect
   */
  public playSound(
    soundKey: string,
    options?: { volume?: number; loop?: boolean }
  ): void {
    if (this.isMuted) {
      return;
    }

    // If audio is locked (no user interaction yet), queue and retry on unlock
    if (this.scene.sound.locked) {
      this.queuedPlays.push({ key: soundKey, options });
      this.scene.sound.once(Phaser.Sound.Events.UNLOCKED, () => {
        const queue = [...this.queuedPlays];
        this.queuedPlays = [];
        queue.forEach(({ key, options: opts }) => this.playSound(key, opts));
      });
      return;
    }

    const sound = this.sounds.get(soundKey);
    if (sound) {
      const soundInstance = sound as Phaser.Sound.BaseSound & {
        setVolume?: (volume: number) => void;
        setLoop?: (loop: boolean) => void;
        isDecoded?: boolean;
        play: () => void;
      };

      // Apply options if provided
      if (options?.volume !== undefined) {
        soundInstance.setVolume?.(options.volume * this.sfxVolume);
      }
      if (options?.loop !== undefined) {
        soundInstance.setLoop?.(options.loop);
      }

      // Play now if decoded; otherwise wait for decode event
      const soundManager = this.scene.sound as Phaser.Sound.BaseSoundManager & {
        usingWebAudio?: boolean;
      };
      if (soundManager.usingWebAudio && !soundInstance.isDecoded) {
        this.scene.sound.once(Phaser.Sound.Events.DECODED, (key: string) => {
          if (key === soundKey) {
            soundInstance.play();
          }
        });
        return;
      }
      soundInstance.play();
    }
  }

  /**
   * Stop specific sound
   */
  public stopSound(soundKey: string): void {
    const sound = this.sounds.get(soundKey);
    if (sound) {
      (sound as Phaser.Sound.BaseSound).stop();
    }
  }

  /**
   * Stop all sounds
   */
  public stopAllSounds(): void {
    this.sounds.forEach((sound) => {
      (sound as Phaser.Sound.BaseSound).stop();
    });
    this.stopBackgroundMusic();
  }

  /**
   * Toggle mute state
   */
  public toggleMute(): boolean {
    this.isMuted = !this.isMuted;

    if (this.isMuted) {
      this.stopAllSounds();
    } else {
      // Resume background music if it was playing
      if (this.backgroundMusic) {
        this.backgroundMusic.play();
      }
    }

    return this.isMuted;
  }

  /**
   * Set master volume
   */
  public setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    this.updateAllVolumes();
  }

  /**
   * Set SFX volume
   */
  public setSfxVolume(volume: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
    this.updateSfxVolumes();
  }

  /**
   * Set music volume
   */
  public setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    this.updateMusicVolumes();
  }

  /**
   * Update all sound volumes
   */
  private updateAllVolumes(): void {
    this.updateSfxVolumes();
    this.updateMusicVolumes();
  }

  /**
   * Update SFX volumes
   */
  private updateSfxVolumes(): void {
    const sfxKeys = [
      "start-music",
      "eating-ghost",
      "killed-by-ghost",
      "ghost-normal-move",
      "ghost-scatter-mode",
      "ghost-return-home",
      "new-life",
      "credit-sound",
    ];

    sfxKeys.forEach((key) => {
      const sound = this.sounds.get(key);
      if (sound) {
        const baseVolume =
          key.includes("ghost-normal-move") ||
          key.includes("ghost-scatter-mode")
            ? 0.5
            : 1;
        const soundInstance = sound as Phaser.Sound.BaseSound & {
          setVolume?: (volume: number) => void;
        };
        soundInstance.setVolume?.(
          this.sfxVolume * baseVolume * this.masterVolume
        );
      }
    });
  }

  /**
   * Update music volumes
   */
  private updateMusicVolumes(): void {
    const musicKeys = ["coffee-break"];

    musicKeys.forEach((key) => {
      const sound = this.sounds.get(key);
      if (sound) {
        const soundInstance = sound as Phaser.Sound.BaseSound & {
          setVolume?: (volume: number) => void;
        };
        soundInstance.setVolume?.(this.musicVolume * this.masterVolume);
      }
    });
  }

  /**
   * Get current mute state
   */
  public isMutedState(): boolean {
    return this.isMuted;
  }

  /**
   * Get master volume
   */
  public getMasterVolume(): number {
    return this.masterVolume;
  }

  /**
   * Get SFX volume
   */
  public getSfxVolume(): number {
    return this.sfxVolume;
  }

  /**
   * Get music volume
   */
  public getMusicVolume(): number {
    return this.musicVolume;
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    this.stopAllSounds();
    this.sounds.clear();
    this.backgroundMusic = null;
  }
}
